import React, { useState } from 'react';
import './Auth.css';

/**
 * LoginPage
 * @param {fn} onLogin      - (credentials) => Promise<boolean>
 * @param {fn} onGoRegister - navigate to register page
 * @param {string} error    - error message from useAuth
 * @param {boolean} loading - loading state
 */
export default function LoginPage({ onLogin, onGoRegister, error, loading }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setFieldErrors((fe) => ({ ...fe, [e.target.name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email';
    if (!form.password.trim()) errs.password = 'Password is required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    await onLogin(form);
  };

  const fillDemo = (email, password) => setForm({ email, password });

  return (
    <div className="auth-shell">
      {/* Left panel – branding */}
      <div className="auth-brand">
        <div className="auth-brand__inner">
          <div className="auth-brand__logo">
            <span className="auth-brand__logo-icon">🌽</span>
          </div>
          <h1 className="auth-brand__title">Maaze</h1>
          <p className="auth-brand__tagline">Popcorn Seed Management System</p>

          <div className="auth-brand__stats">
            {[
              { icon: '👨‍🌾', val: '248+', label: 'Active Farmers' },
              { icon: '🏢', val: '4', label: 'Organisers' },
              { icon: '🏘', val: '10', label: 'Villages' },
              { icon: '🌾', val: '12,400', label: 'kg Distributed' },
            ].map((s) => (
              <div className="auth-brand__stat" key={s.label}>
                <span className="auth-brand__stat-icon">{s.icon}</span>
                <span className="auth-brand__stat-val">{s.val}</span>
                <span className="auth-brand__stat-label">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="auth-brand__flow">
            {['Fund', 'Purchase', 'Distribute', 'Cultivate', 'Collect', 'Process', 'Deliver'].map((s, i, arr) => (
              <React.Fragment key={s}>
                <span className="auth-brand__flow-step">{s}</span>
                {i < arr.length - 1 && <span className="auth-brand__flow-sep">›</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Decorative background blobs */}
        <div className="auth-brand__blob auth-brand__blob--1" />
        <div className="auth-brand__blob auth-brand__blob--2" />
      </div>

      {/* Right panel – form */}
      <div className="auth-form-panel">
        <div className="auth-form-card">

          {/* Header */}
          <div className="auth-form-card__header">
            <div className="auth-form-card__mobile-logo">🌽 Maaze</div>
            <h2 className="auth-form-card__title">Welcome back</h2>
            <p className="auth-form-card__sub">Sign in to your account to continue</p>
          </div>

          {/* Demo credentials */}
          <div className="auth-demo-creds">
            <div className="auth-demo-creds__label">Quick Demo Login:</div>
            <div className="auth-demo-creds__btns">
              {[
                { label: 'Owner', email: '', pass: '' },
                { label: 'Organiser', email: '', pass: '' },
                // { label: 'Admin', email: '', pass: '' },
              ].map((d) => (
                <button key={d.label} className="auth-demo-btn" onClick={() => fillDemo(d.email, d.pass)} type="button">
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <form className="auth-form" onSubmit={handleSubmit} noValidate>

            {/* Global error */}
            {error && (
              <div className="auth-error-banner">
                <span>⚠️</span> {error}
              </div>
            )}

            {/* Email */}
            <div className="auth-field">
              <label className="auth-field__label">Email Address</label>
              <div className={`auth-field__wrap ${fieldErrors.email ? 'auth-field__wrap--error' : ''}`}>
                <span className="auth-field__icon">✉️</span>
                <input
                  className="auth-field__input"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </div>
              {fieldErrors.email && <span className="auth-field__error">{fieldErrors.email}</span>}
            </div>

            {/* Password */}
            <div className="auth-field">
              <label className="auth-field__label">Password</label>
              <div className={`auth-field__wrap ${fieldErrors.password ? 'auth-field__wrap--error' : ''}`}>
                <span className="auth-field__icon">🔒</span>
                <input
                  className="auth-field__input"
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                />
                <button type="button" className="auth-field__toggle" onClick={() => setShowPass((s) => !s)}>
                  {showPass ? '🙈' : '👁'}
                </button>
              </div>
              {fieldErrors.password && <span className="auth-field__error">{fieldErrors.password}</span>}
            </div>

            {/* Forgot password link */}
            <div className="auth-form__row">
              <button type="button" className="auth-link">Forgot password?</button>
            </div>

            {/* Submit */}
            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? (
                <><span className="auth-spinner" /> Signing in…</>
              ) : (
                'Sign In →'
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="auth-form-card__footer">
            Don't have an account?{' '}
            <button type="button" className="auth-link auth-link--accent" onClick={onGoRegister}>
              Create account
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
